import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Modal } from '@/shared/ui/Modal';
import { Skeleton } from '@/shared/ui/Skeleton';
import { formatDate } from '@/shared/utils/format';
import type { CommunityState, PartnerDetail as PartnerDetailData } from '../api/communityApi';
import { useCommunityActions, usePartner } from '../hooks/useCommunity';
import styles from './Community.module.css';

/**
 * Partner detail (click a network row): touch history, rhythm editor, mark a planned touch done or
 * skipped, log a touch, monthly referral counts, archive. Opens as a side panel so the table stays.
 */
export function PartnerDetail({
  partnerId,
  vocabulary,
  onClose,
  onPlan,
}: {
  partnerId: string | null;
  vocabulary: CommunityState['vocabulary'];
  onClose: () => void;
  onPlan: (partner: PartnerDetailData) => void;
}) {
  const { data, isLoading } = usePartner(partnerId);
  const actions = useCommunityActions();
  const [logging, setLogging] = useState(false);
  const [logActivity, setLogActivity] = useState('coffee');
  const [logDate, setLogDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const logDateValid = /^\d{4}-\d{2}-\d{2}$/.test(logDate) && !Number.isNaN(new Date(`${logDate}T12:00:00`).getTime());
  const [logNotes, setLogNotes] = useState('');
  // The panel stays mounted from one partner to the next: the log form never carries over.
  useEffect(() => {
    setLogging(false);
    setLogActivity('coffee');
    setLogNotes('');
  }, [partnerId]);
  const openLog = () => {
    setLogDate(format(new Date(), 'yyyy-MM-dd'));
    setLogging(true);
  };
  const busy = actions.touchStatus.isPending || actions.logTouch.isPending || actions.updatePartner.isPending;

  const submitLog = () => {
    if (!data) return;
    actions.logTouch.mutate(
      { partnerId: data.id, activityType: logActivity, date: new Date(`${logDate}T12:00:00`).toISOString(), notes: logNotes.trim() || null },
      { onSuccess: () => { setLogging(false); setLogNotes(''); } },
    );
  };

  return (
    <Modal isOpen={partnerId !== null} onClose={onClose} title={data?.name ?? 'Partner'} variant="side" size="md">
      {isLoading || !data ? (
        <Skeleton height={200} />
      ) : (
        <div className={styles.form}>
          <div className={styles.partnerRole}>{[data.role, data.org, data.address].filter(Boolean).join(' · ') || data.categoryLabel}</div>
          <div className={styles.detailGrid}>
            <div className={styles.tile}>
              <div className={styles.tileLabel}>Last touch</div>
              <div className={styles.tileValue}>{data.lastTouch ? `${data.lastTouch.label} · ${formatDate(data.lastTouch.date, 'MMM d')}` : 'None yet'}</div>
            </div>
            <div className={styles.tile}>
              <div className={styles.tileLabel}>Your rhythm</div>
              <div className={styles.tileValue}>{data.rhythmLabel}</div>
            </div>
            <div className={styles.tile}>
              <div className={styles.tileLabel}>Status</div>
              <div className={styles.tileValue}>{data.statusLabel}</div>
            </div>
          </div>

          <div>
            <div className={styles.fieldLabel}>Remind me to reconnect</div>
            <div className={styles.chips}>
              {vocabulary.rhythms.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  className={clsx(styles.chip, data.rhythm === r.key && styles.chipActive)}
                  disabled={busy}
                  onClick={() => actions.updatePartner.mutate({ partnerId: data.id, rhythm: r.key, rhythmDays: r.key === 'custom' ? data.rhythmDays ?? 90 : null })}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.inlineActions}>
            <Button variant="primary" size="sm" onClick={() => onPlan(data)}>
              Plan a touch
            </Button>
            <Button variant="secondary" size="sm" onClick={() => (logging ? setLogging(false) : openLog())}>
              Log a touch
            </Button>
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => actions.updatePartner.mutate({ partnerId: data.id, archived: true }, { onSuccess: onClose })}>
              Archive
            </Button>
          </div>

          {logging && (
            <div className={styles.form}>
              <div className={styles.chips}>
                {vocabulary.touchTypes.map((t) => (
                  <button key={t.key} type="button" className={clsx(styles.chip, logActivity === t.key && styles.chipActive)} onClick={() => setLogActivity(t.key)}>
                    {t.label}
                  </button>
                ))}
              </div>
              <Input type="date" label="When" value={logDate} onChange={(e) => setLogDate(e.target.value)} />
              <textarea className={styles.textarea} placeholder="Notes (only you see this)" value={logNotes} onChange={(e) => setLogNotes(e.target.value)} rows={2} />
              <div className={styles.inlineActions}>
                <Button variant="primary" size="sm" loading={actions.logTouch.isPending} disabled={!logDateValid} onClick={submitLog}>
                  Save as done
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setLogging(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          <div>
            <div className={styles.panelKicker}>Touch history</div>
            {data.touches.length === 0 ? (
              <div className={styles.joeBlurb}>No touches yet.</div>
            ) : (
              data.touches.map((t) => (
                <div key={t.id} className={styles.touchRow}>
                  <div>
                    <div>
                      {t.label} · {formatDate(t.date, 'MMM d, yyyy')} ·{' '}
                      <span className={styles.cellMuted}>{t.status === 'planned' ? 'Planned' : t.status === 'done' ? 'Done' : 'Skipped'}</span>
                      {t.status === 'planned' && t.draftTaskId && (
                        <span className={styles.cellMuted}>
                          {' '}· <Link to={`/tasks?task=${encodeURIComponent(t.draftTaskId)}`}>Joe's draft is in Tasks</Link>
                        </span>
                      )}
                      {t.status === 'planned' && t.draftRequested && !t.draftTaskId && <span className={styles.cellMuted}> · Joe is drafting it</span>}
                    </div>
                    {t.notes && <div className={styles.touchNotes}>{t.notes}</div>}
                  </div>
                  {t.status === 'planned' && (
                    <div className={styles.inlineActions}>
                      <Button variant="secondary" size="sm" disabled={busy} onClick={() => actions.touchStatus.mutate({ touchId: t.id, status: 'done', date: new Date().toISOString() })}>
                        Mark done
                      </Button>
                      <Button variant="ghost" size="sm" disabled={busy} onClick={() => actions.touchStatus.mutate({ touchId: t.id, status: 'skipped' })}>
                        Skipped
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          <div>
            <div className={styles.panelKicker}>Referrals you confirmed</div>
            {data.referralCounts.length === 0 ? (
              <div className={styles.joeBlurb}>No months confirmed yet. Blank months stay blank; nothing is ever counted as zero for you.</div>
            ) : (
              data.referralCounts.map((c) => (
                <div key={c.month} className={styles.monthRow}>
                  <span>{c.month}</span>
                  <span className={styles.cellMuted}>{c.count === null ? '-' : `${c.count} · confirmed by you`}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
